import cloneDeep from 'lodash/cloneDeep';
import ELK from 'elkjs/lib/elk.bundled';
import {
  isNode,
} from 'react-flow-renderer';
import merge from 'lodash/merge';

export const MIN_NODE_HEIGHT = 40;
export const MIN_NODE_WIDTH = 220;
const INTER_EDGE_NODE_MARGIN = 150; // This is the width of space that the labels occupy
const INTER_NODE_SPACING = 100; // This is the mount of space between branches
const LAYOUT_OPTIONS = {
  // # GENERAL OPTIONS
  'elk.algorithm': 'layered',
  'elk.alignment': 'RIGHT',
  'elk.direction': 'RIGHT',
  'elk.edgeRouting': 'ORTHOGONAL',
  // # LAYERED OPTIONS
  'elk.layered.considerModelOrder': 'PREFER_EDGES',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.mergeEdges': true,
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.spacing.edgeNodeBetweenLayers': INTER_EDGE_NODE_MARGIN,
  'elk.layered.spacing.nodeNodeBetweenLayers': MIN_NODE_WIDTH,
  // -- NOTE: nodePlacement.bk.<property> settings only take effect if the
  // -- nodePlacement.strategy is 'BRANDES_KOEPF'
  'elk.layered.nodePlacement.bk.fixedAlignment': 'RIGHTDOWN',
  'elk.layered.nodePlacement.bk.edgeStraightening': 'IMPROVE_STRAIGHTNESS',
  // # SPACING
  'elk.spacing.nodeNode': INTER_NODE_SPACING,
};

/**
 * buildELKGraph does some preprocessing to prepare the Nodes/Edges for passing
 * into the ELK service itself in order to lay them out
 * @param {Array} elements - An array of React Flow Nodes and Edges that we
 * want to layout
 * @returns {Object}
 */
const buildELKGraph = (elements, layoutOptions = {}) => {
  const graph = {
    id: 'root',
    layoutOptions: merge(LAYOUT_OPTIONS, layoutOptions),
    children: [],
    edges: [],
  };

  const nodes = {};

  elements.forEach(element => {
    if (isNode(element)) {
      const node = {
        ...element,
        height: element.height ?? MIN_NODE_HEIGHT,
        width: element.width ?? MIN_NODE_WIDTH,
      };

      nodes[node.id] = node;

      graph.children.push(node);
    } else {
      graph.edges.push({ ...element });
    }
  });

  return graph;
};

/**
 * layoutGraph initiates the ELK service in order to figure out the positions
 * of the React Flow nodes for the Journey graph
 * @param {Array} rawElements - An array of React Flow Nodes and Edges that we
 * want to layout
 * @returns {Promise}
 */
const layoutGraph = (rawElements, elkOptions = {}) => {
  const elk = new ELK();
  const copy = cloneDeep(rawElements);
  const elkGraph = buildELKGraph(copy, elkOptions);

  return elk.layout(elkGraph).then(graph => copy.map(elementCopy => {
    if (isNode(elementCopy)) {
      const nodeWithPosition = graph.children.find(
        element => element.id === elementCopy.id,
      );

      elementCopy.targetPosition = 'left';
      elementCopy.sourcePosition = 'right';
      // show after layout to prevent flicker
      elementCopy.data.hide = false;
      // unfortunately we need this little hack to pass a slightly different position
      // to notify react flow about the change.
      elementCopy.position = {
        x: nodeWithPosition.x + (Math.random() / 1000),
        y: nodeWithPosition.y,
      };
    } else {
      // show after layout to prevent flicker
      elementCopy.hidden = false;
    }
    return elementCopy;
  }));
};

export { layoutGraph, buildELKGraph };
